// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — an organisation's own layers drawn on a globe (Situation room,
 * slice CR5): each layer a GeoJSON source with its polygons filled and
 * outlined, its lines and its points, in the layer's colour, and the
 * features' names written beside them from zoom 5. Vector layers stay sharp
 * however close the camera comes, so a globe showing one may zoom further
 * (decision CR-D3, "by data resolution"), but never to 10, where MapLibre's
 * globe turns into Mercator.
 *
 * Idempotent: `drawOwnLayers` is given the whole list each time and adds,
 * recolours and removes to match.
 */

import type { Map as MaplibreMap, GeoJSONSource } from 'maplibre-gl'
import { setFineReason } from './globeZoomCeiling'

export interface OwnLayerDraw {
  id: string
  data: GeoJSON.FeatureCollection
  /** CSS colour. */
  color: string
}

/** The zoom a globe showing a vector layer may reach (shared with fine datasets, see globeZoomCeiling). */
export { FINE_DATA_MAX_ZOOM } from './globeZoomCeiling'

const PREFIX = 'own-layer-'
const PARTS = ['fill', 'line', 'point', 'label'] as const

const drawn = new WeakMap<MaplibreMap, Map<string, { color: string; data: GeoJSON.FeatureCollection }>>()

function sourceId(id: string): string {
  return `${PREFIX}${id}`
}

function remove(map: MaplibreMap, id: string): void {
  for (const part of PARTS) {
    const layer = `${sourceId(id)}-${part}`
    if (map.getLayer(layer)) map.removeLayer(layer)
  }
  if (map.getSource(sourceId(id))) map.removeSource(sourceId(id))
}

function add(map: MaplibreMap, layer: OwnLayerDraw): void {
  // Whatever a refused earlier try left behind goes first, so a retry starts clean.
  remove(map, layer.id)
  const source = sourceId(layer.id)
  map.addSource(source, { type: 'geojson', data: layer.data })
  map.addLayer({
    id: `${source}-fill`, type: 'fill', source,
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: { 'fill-color': layer.color, 'fill-opacity': 0.22 },
  })
  map.addLayer({
    id: `${source}-line`, type: 'line', source,
    filter: ['in', ['geometry-type'], ['literal', ['Polygon', 'LineString']]],
    paint: { 'line-color': layer.color, 'line-width': 2 },
  })
  map.addLayer({
    id: `${source}-point`, type: 'circle', source,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: { 'circle-color': layer.color, 'circle-radius': 5, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.5 },
  })
  map.addLayer({
    id: `${source}-label`, type: 'symbol', source, minzoom: 5,
    layout: {
      'text-field': ['coalesce', ['get', 'name'], ''],
      // A font the glyph server has (OpenFreeMap serves Noto Sans; Open Sans and the default stack answer 404).
      'text-font': ['Noto Sans Regular'],
      'text-size': 12,
      'text-offset': [0, 1.1],
      'text-anchor': 'top',
      'text-optional': true,
    },
    paint: { 'text-color': '#ffffff', 'text-halo-color': 'rgba(0, 0, 0, 0.75)', 'text-halo-width': 1.5 },
  })
}

/**
 * Make the globe show exactly these own layers, in this order (the last on
 * top), and set how close its camera may come. False when the map's style
 * is not loaded yet (call again once it is).
 */
export function drawOwnLayers(map: MaplibreMap, layers: readonly OwnLayerDraw[]): boolean {
  // Not `isStyleLoaded()`: it also waits for every source's tiles, and a globe
  // streaming clouds or data always has one loading. Only a style still being
  // built refuses a source (it throws); the caller tries again when the map is idle.
  try {
    return draw(map, layers)
  } catch {
    return false
  }
}

function draw(map: MaplibreMap, layers: readonly OwnLayerDraw[]): boolean {
  if (!map.getStyle()) return false
  let held = drawn.get(map)
  if (!held) {
    held = new Map()
    drawn.set(map, held)
  }
  const wanted = new Set(layers.map((l) => l.id))
  for (const id of [...held.keys()]) {
    if (!wanted.has(id)) {
      remove(map, id)
      held.delete(id)
    }
  }
  for (const layer of layers) {
    const had = held.get(layer.id)
    if (had && had.color === layer.color) {
      if (had.data !== layer.data) (map.getSource(sourceId(layer.id)) as GeoJSONSource | undefined)?.setData(layer.data)
    } else {
      if (had) remove(map, layer.id)
      add(map, layer)
    }
    held.set(layer.id, { color: layer.color, data: layer.data })
  }
  // The order asked for: each layer's parts moved to the top in turn.
  if (layers.length > 1) {
    for (const layer of layers) for (const part of PARTS) map.moveLayer(`${sourceId(layer.id)}-${part}`)
  }
  setFineReason(map, 'ownLayers', layers.length > 0)
  return true
}

/** The names and descriptions of own-layer features under a point on the screen, nearest layer first. */
export function ownFeaturesAt(map: MaplibreMap, point: { x: number; y: number }): Array<{ name: string; description: string }> {
  const ids = (map.getStyle()?.layers ?? []).map((l) => l.id).filter((id) => id.startsWith(PREFIX) && !id.endsWith('-label'))
  if (ids.length === 0) return []
  const box: [[number, number], [number, number]] = [[point.x - 4, point.y - 4], [point.x + 4, point.y + 4]]
  const seen = new Set<string>()
  const out: Array<{ name: string; description: string }> = []
  for (const f of map.queryRenderedFeatures(box, { layers: ids })) {
    const name = typeof f.properties?.name === 'string' ? f.properties.name : ''
    const description = typeof f.properties?.description === 'string' ? f.properties.description : ''
    const key = `${name}\u0000${description}`
    if ((!name && !description) || seen.has(key)) continue
    seen.add(key)
    out.push({ name, description })
  }
  return out
}
